import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Camera, Check, CircleAlert, Clock } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { CaptureButtons } from "@/components/media/CaptureButtons";
import { MediaThumb } from "@/components/media/MediaThumb";
import { CertificatePanel } from "@/components/CertificatePanel";
import { MaintenancePanel } from "@/components/MaintenancePanel";
import { ProgressBar, StatusPill } from "@/components/Progress";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { STAGE_LABEL, STATUS_LABEL, useLocale } from "@/lib/locale";
import { captionOf, MediaTooLarge, prepareUpload } from "@/lib/media";
import { cn } from "@/lib/utils";
import type { Stage, SurfaceDetail } from "@/lib/domain";

export default function SurfaceDetailPage() {
  const { id = "" } = useParams();
  const { user } = useAuth();
  const { t, locale } = useLocale();
  const [surface, setSurface] = useState<SurfaceDetail | null>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [uploadNote, setUploadNote] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<{ stageId: string; message: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const detail = await api.surface(id);
      setSurface(detail);
      setStages(detail.stages);
    } catch {
      setSurface(null);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function setStage(
    stage: Stage,
    status: "reported" | "approved" | "issue" | "in_progress",
  ) {
    setBusyId(stage.id);
    try {
      await api.updateStage(stage.id, { status });
      await load();
    } finally {
      setBusyId(null);
    }
  }

  /** Photos and videos from the phone camera (or gallery) on one stage. */
  async function uploadMedia(stage: Stage, files: File[]) {
    setBusyId(stage.id);
    setUploadError(null);
    try {
      let done = 0;
      for (const f of files) {
        const n = files.length > 1 ? ` ${done + 1}/${files.length}` : "";
        setUploadNote(`${t.uploading}${n}…`);
        const { body, filename } = await prepareUpload(f);
        await api.upload(
          body,
          {
            kind: stage.code === "handover" ? "collaudo_photo" : "progress_photo",
            surfaceId: id,
            stageId: stage.id,
            caption: captionOf(f) || stage.code,
          },
          { filename, onProgress: (p) => setUploadNote(`${t.uploading}${n} ${Math.round(p * 100)}%`) },
        );
        done += 1;
      }
    } catch (e) {
      setUploadError({ stageId: stage.id, message: e instanceof MediaTooLarge ? "Video > 500 MB" : "Upload failed" });
    } finally {
      setUploadNote(null);
      setBusyId(null);
      await load();
    }
  }

  if (!surface) {
    return (
      <AppShell>
        <p className="text-sm text-ice/60">Caricamento…</p>
      </AppShell>
    );
  }

  const isAdmin = user?.role === "admin";

  return (
    <AppShell>
      <Link
        to={`/projects/${surface.project.id}`}
        className="text-[0.62rem] uppercase tracking-[0.18em] text-ice/55 hover:text-paper"
      >
        ← {surface.project.name}
      </Link>

      <header className="mt-3 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="brand-kicker">
            {surface.surfaceType.replace(/_/g, " ")}
            {surface.installer ? ` · ${surface.installer.companyName}` : ""}
          </div>
          <h1 className="font-serif text-2xl text-paper sm:text-3xl">
            {surface.name}
          </h1>
          <p className="mt-1 text-[0.76rem] text-ice/65">
            {surface.m2} m² · {surface.finishEffect.replace(/_/g, " ")}
            {surface.colorRef ? ` · ${surface.colorRef}` : ""} ·{" "}
            {surface.systemVariants.join(" · ")}
          </p>
          {surface.notes && <p className="mt-2 max-w-xl whitespace-pre-line text-[0.76rem] text-ice/70">{surface.notes}</p>}
        </div>
        <div className="min-w-[220px]">
          <StatusPill
            status={surface.status}
            label={STATUS_LABEL[locale][surface.status] ?? surface.status}
            className="mb-2"
          />
          <ProgressBar progress={surface.progress} />
        </div>
      </header>

      <hr className="hairline my-6" />

      <h2 className="text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-goldbright">
        {t.stages}
      </h2>

      <ol className="mt-4 grid gap-3">
        {stages.map((stage) => {
          const busy = busyId === stage.id;
          return (
            <li key={stage.id} className="glass-panel-strong p-4">
              <div className="flex flex-wrap items-start gap-3">
                <span
                  className={cn(
                    "num mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full border text-[0.62rem]",
                    stage.status === "approved"
                      ? "border-green/50 text-green"
                      : stage.status === "reported"
                        ? "border-pink/50 text-pink"
                        : stage.status === "issue"
                          ? "border-rose/60 text-pink"
                          : "border-white/20 text-ice/55",
                  )}
                >
                  {stage.position}
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-serif text-[0.95rem] text-paper">
                      {STAGE_LABEL[locale][stage.code] ?? stage.code}
                    </span>
                    <StatusPill
                      status={stage.status}
                      label={STATUS_LABEL[locale][stage.status] ?? stage.status}
                    />
                  </div>

                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[0.66rem] text-ice/55">
                    {stage.reportedAt && (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {t.workReported}: {stage.reportedAt.slice(0, 10)}
                        {stage.reportedBy ? ` · ${stage.reportedBy.name}` : ""}
                      </span>
                    )}
                    {stage.approvedAt && (
                      <span className="inline-flex items-center gap-1 text-green">
                        <Check className="h-3 w-3" />
                        {t.reviewApproved}: {stage.approvedAt.slice(0, 10)}
                        {stage.approvedBy ? ` · ${stage.approvedBy.name}` : ""}
                      </span>
                    )}
                    {stage.notes && <span>{stage.notes}</span>}
                  </div>

                  {stage.media.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {stage.media.map((m) => (
                        <MediaThumb key={m.id} url={m.url ?? "#"} mimeType={m.mimeType} caption={m.caption} className="h-16 w-16" />
                      ))}
                    </div>
                  )}

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setStage(stage, "reported")}
                      className="inline-flex items-center gap-1.5 rounded-full border border-pink/50 bg-pink/10 px-3 py-1.5 text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-paper transition hover:bg-pink/20 disabled:opacity-50"
                    >
                      <Check className="h-3 w-3" />
                      {t.report}
                    </button>

                    {isAdmin && (
                      <>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => setStage(stage, "approved")}
                          className="inline-flex items-center gap-1.5 rounded-full border border-green/50 bg-green/10 px-3 py-1.5 text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-green transition hover:bg-green/20 disabled:opacity-50"
                        >
                          <Check className="h-3 w-3" />
                          {t.approve}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => setStage(stage, "issue")}
                          className="inline-flex items-center gap-1.5 rounded-full border border-rose/60 bg-rose/15 px-3 py-1.5 text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-pink transition hover:bg-rose/25 disabled:opacity-50"
                        >
                          <CircleAlert className="h-3 w-3" />
                          {t.raiseIssue}
                        </button>
                      </>
                    )}

                    <CaptureButtons
                      size="xs"
                      disabled={busy}
                      busyLabel={busy ? uploadNote : null}
                      labels={{ photo: t.photo, video: t.video, library: t.library }}
                      onFiles={(files) => uploadMedia(stage, files)}
                    />
                    {uploadError?.stageId === stage.id && <span className="text-[0.6rem] text-pink">{uploadError.message}</span>}

                    {stage.media.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-[0.6rem] text-ice/50">
                        <Camera className="h-3 w-3" />
                        {stage.media.length}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      <CertificatePanel
        surfaceId={surface.id}
        qrToken={surface.qrToken}
        handoverApproved={
          stages.find((s) => s.code === "handover")?.status === "approved"
        }
        isAdmin={isAdmin}
        onSurfaceChanged={load}
      />

      <MaintenancePanel
        surfaceId={surface.id}
        canAct
        hasCertificate={surface.status === "certified" || surface.status === "revoked"}
      />
    </AppShell>
  );
}
